import hash from 'object-hash';
import { DB } from './indexedDB.js';

/**
 * Calculates the hash of an object
 * 
 * @param {object} payload  the report payload object
 * @returns string          hash of the payload object
 */
export function calculateHash(payload) {
    return hash(payload);
}

/**
 * Generates a unique key for a report payload
 * 
 * @param {object} payload  the report payload object
 * @returns string          unique key
 */
export function generateKey(payload) {
    return `tmpreport_uid${payload.uid}-pid${payload.planning_id}-wid${payload.work_id}`;
}

/**
 * Explodes the key of a report payload
 *
 * @param {string} key  the report payload key
 * @returns object       the report payload object
 */
export function explodeKey(key) {
    return {
        uid: key.split('-')[1].split('uid')[1],
        planning_id: key.split('-')[2].split('pid')[1],
        work_id: key.split('-')[3].split('wid')[1],
    };
}

/**
 * Creates a temporary record for a report payload
 * 
 * @param {object} payload  the report payload object
 * @param {object} context  the context object (Extra data related to the payload)
 * @returns object          the temporary record object
 */
export function makeTmpRecordReport(payload, context = null) {
    let tmpReport = {
        hash: calculateHash(payload),
        status: 'pending',
        date: new Date(),
        context: context,
        payload: payload,
    };

    return {
        key: generateKey(payload),
        value: tmpReport
    };
}

/**
 * Stores a report payload in local storage
 * 
 * @param {object} payload  the report payload object
 * @param {object} context  the context object (Extra data related to the payload)
 * @returns string          the key of the stored report payload
 */
export function storeReportPayload(payload, context = null) {
    const tmp = makeTmpRecordReport(payload, context);
    DB.setItem(tmp.key, tmp.value);
    console.info('Stored report payload:', tmp.key);
    return tmp.key;
}

/**
 * Retrieves a report payload from local storage
 * 
 * @param {string} key  the report payload key
 * @returns object       the report payload object
 */
export function getStoredReportPayload(key) {
    const stored = DB.getItem(key);
    return stored;
}

/**
 * Deletes a report payload from local storage
 * 
 * @param {string} key  the report payload key
 */
export function deleteStoredReportPayload(key) {
    console.info('Removing stored report with key', key);
    DB.removeItem(key);
}

/**
 * Finds stored report payloads by user id
 * 
 * @param {number} userId  the user id
 * @returns array           the report payload keys
 */
export function findStoredReportByUserId(userId) {
    const keys = Object.keys(DB);
    const filtered = keys.filter(key => key.includes(`uid${userId}`));
    return filtered;
}

/**
 * Finds stored report payloads by planning id
 * 
 * @param {number} planningId  the planning id
 * @returns array               the report payload keys
 */
export function findStoredReportByPlanningId(planningId) {
    const keys = Object.keys(DB);
    const filtered = keys.filter(key => key.includes(`pid${planningId}`));
    return filtered;
}

/**
 * Finds stored report payloads by work id
 * 
 * @param {number} workId  the work id
 * @returns array           the report payload keys
 */
export function findStoredReportByWorkId(workId) {
    const keys = Object.keys(DB);
    const filtered = keys.filter(key => key.includes(`wid${workId}`));
    return filtered;
}